/**
 * Pros Cons List App
 */

import { nanoid } from '@reduxjs/toolkit'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { EmptyStatePanel } from 'pcl-ui'
import React, { useEffect, useState } from 'react'
import { track } from 'track'
import { AddOptionForm } from '@/components/AddOptionForm'
import { Option } from '@/components/Option'
import { PageTitle } from '@/components/PageTitle'
import { Panel } from '@/components/Panel'
import i18nextConfig from '@/next-i18next.config'
import { config } from '@/site.config.js'
import { useAppDispatch, useAppSelector } from '@/state/hooks'
import { localSavedStatesStore } from '@/state/localSavedStatesStore'
import {
  addOption,
  addReasons,
  clearGenerationError,
  ListType,
  replaceMetaState,
  replaceOptionsState,
  replaceReasonsState,
  setAiDisabled,
  setGenerationError,
  setIsGenerating,
  setLastAddedOptionId,
} from '@/state/reducers'
import { RootState } from '@/state/store'
import { isAiDisabled as checkAiDisabled } from '@/utils/aiFeatureStatus'
import { generateReasons } from '@/utils/generateReasonsApi'
import { getStaticPaths, makeStaticProps } from '@/utils/getStatic'
import languageDetector from '@/utils/languageDetector'

function Home() {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const [mounted, setMounted] = useState(false)
  const [listNotFound, setListNotFound] = useState(false)
  const metaKey = useAppSelector((state: RootState) => state.meta.key)

  const ALL_CHIP_KEYS = [
    'chipWorkingFromHome',
    'chipUsingSocialMedia',
    'chipGettingAPet',
    'chipBuyingACar',
    'chipStartingABusiness',
    'chipChangingJobs',
    'chipTakingAVacation',
  ]

  const NUMBER_OF_CHIPS_TO_SHOW = 3
  const randomizeChips =
    process.env.FEATURES_RANDOMIZE_EMPTY_STATE_CHIPS === 'true'

  const [selectedChipKeys, setSelectedChipKeys] = useState<string[]>(
    randomizeChips ? [] : ALL_CHIP_KEYS.slice(0, NUMBER_OF_CHIPS_TO_SHOW),
  )

  useEffect(() => {
    const cachedLocale = localStorage.getItem('i18nextLng')
    if (
      router.query.locale === i18nextConfig.i18n.defaultLocale &&
      languageDetector.cache
    ) {
      languageDetector.cache(i18nextConfig.i18n.defaultLocale)
      router.replace(router.pathname.replace(`[locale]`, ''))
    } else if (router.query.locale && languageDetector.cache) {
      languageDetector.cache(router.query.locale.toString())
    } else if (
      cachedLocale &&
      cachedLocale !== i18nextConfig.i18n.defaultLocale
    ) {
      router.replace({
        pathname: '/[locale]',
        query: { locale: cachedLocale },
      })
    }
  }, [router])

  useEffect(() => {
    setMounted(true)
    dispatch(setAiDisabled(checkAiDisabled()))

    const match = window.location.hash.match(/^#list=(.+)$/)
    if (match) {
      const key = decodeURIComponent(match[1])
      localSavedStatesStore.getItem<ListType>(key).then((item) => {
        if (item) {
          dispatch(replaceOptionsState(item.options))
          dispatch(replaceReasonsState(item.reasons))
          dispatch(replaceMetaState(item.meta))
          track('event', 'load-list-from-hash')
        } else {
          setListNotFound(true)
          track('event', 'load-list-from-hash-not-found')
        }
      })
    }

    if (randomizeChips) {
      const shuffled = [...ALL_CHIP_KEYS].sort(() => Math.random() - 0.5)
      setSelectedChipKeys(shuffled.slice(0, NUMBER_OF_CHIPS_TO_SHOW))
    }
  }, [dispatch])

  useEffect(() => {
    if (metaKey) setListNotFound(false)
  }, [metaKey])

  useEffect(() => {
    const handler = () => setListNotFound(false)
    window.addEventListener('pcl:new-list', handler)
    return () => window.removeEventListener('pcl:new-list', handler)
  }, [])

  const options = useAppSelector((state: RootState) => state.options)
  const isGenerating = useAppSelector(
    (state) => state.meta.isGenerating ?? false,
  )
  const aiDisabled = useAppSelector((state) => state.meta.aiDisabled ?? false)

  async function handleChipGenerate(label: string) {
    const optionId = nanoid(4)
    dispatch(setIsGenerating(true))
    dispatch(clearGenerationError())

    try {
      const response = await generateReasons({
        optionLabel: label,
        optionId,
        language: i18n.language,
      })

      dispatch(addOption({ label, id: optionId }))
      dispatch(setLastAddedOptionId(optionId))

      const hydratedReasons = response.reasons.map((reason) => ({
        ...reason,
        optionId,
        weight: 0,
      }))
      dispatch(addReasons(hydratedReasons))
      track('event', 'ai-generate-reasons', {
        reasons_count: response.reasons.length,
        context: 'empty-state-chip',
      })
    } catch (err) {
      dispatch(addOption({ label, id: optionId }))
      const msg =
        err instanceof Error ? err.message : t('AddOptionForm.errorGenerating')
      dispatch(setGenerationError({ optionId, message: msg }))
      dispatch(setAiDisabled(true))
      track('event', 'ai-generate-reasons-error', {
        context: 'empty-state-chip',
      })
    } finally {
      dispatch(setIsGenerating(false))
    }
  }

  return (
    <>
      <PageTitle>{t('title')}</PageTitle>
      <Head>
        <meta name="description" content={t('description')} />
        <meta property="og:description" content={t('description')} />
        <meta property="og:title" content={t('title')} />
        <meta
          property="og:url"
          content={`${config.prodDomain}/${router.asPath}`}
        />
        <meta property="og:site_name" content={t('title')} />
      </Head>

      <div className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-7">
          {listNotFound && (
            <div className="flex items-center justify-between gap-3 mb-5 px-3.5 py-2.5 text-xs border border-amber-400/50 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300">
              <span>{t('Home.listNotFound')}</span>
              <button
                onClick={() => setListNotFound(false)}
                aria-label={t('Option.dismiss')}
                className="shrink-0 opacity-60 hover:opacity-100 transition-opacity bg-transparent border-0 cursor-pointer p-0 text-inherit"
              >
                ✕
              </button>
            </div>
          )}
          <div className="flex flex-col gap-6">
            {options.map((option) => (
              <Panel key={option.id}>
                <Option option={option} />
              </Panel>
            ))}
          </div>

          {/* Add option bottom bar */}
          <div className={options.length > 0 ? 'mt-5' : ''}>
            <AddOptionForm />
          </div>

          {/* Empty state ghost card */}
          {options.length === 0 && (
            <div className="mt-5">
              <EmptyStatePanel
                heading={t('EmptyState.title')}
                hint={t('EmptyState.hint')}
                suggestions={selectedChipKeys.map((key) =>
                  t(`EmptyState.${key}`),
                )}
                onSuggest={(label) => {
                  const id = nanoid(4)
                  dispatch(setLastAddedOptionId(id))
                  dispatch(addOption({ label, id }))
                }}
                onGenerate={
                  process.env.FEATURES_SHOW_AI_FEATURES === 'true' &&
                  !aiDisabled
                    ? handleChipGenerate
                    : undefined
                }
                generateAriaLabel={(label) =>
                  t('EmptyState.generateAriaLabel', { label })
                }
                isGenerating={isGenerating}
                className={
                  randomizeChips
                    ? `transition-opacity duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`
                    : ''
                }
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Home
const getStaticProps = makeStaticProps(['common', 'articles-listing', 'modals'])

export { getStaticPaths, getStaticProps }
